import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useDeleteReferenceRateValue } from 'src/features/referenceRateValues/api/useDeleteReferenceRateValue';
import { LIST_REFERENCE_RATE_VALUES_RESOURCE } from 'src/features/referenceRateValues/api/useListReferenceRateValues';
import { REFERENCE_RATE_RESOURCE } from 'src/features/referenceRates/api/useGetReferenceRate';
import { LIST_REFERENCE_RATES_RESOURCE } from 'src/features/referenceRates/api/useListReferenceRates';
import { referenceRateValuesMessages } from 'src/features/referenceRateValues/utils/messages';
import { ReferenceRateValue } from 'src/features/referenceRateValues/utils/types';

interface DeleteReferenceRateValueFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedReferenceRateValue: ReferenceRateValue;
    children?: React.ReactNode;
}

function DeleteReferenceRateValueFormModal<TData>({
    selectedReferenceRateValue,
    children,
}: DeleteReferenceRateValueFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { referenceRateId = '' } = useParams();

    const onError = useOnError();

    const { mutate: deleteReferenceRateValue } = useDeleteReferenceRateValue({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATE_VALUES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [REFERENCE_RATE_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_REFERENCE_RATES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(referenceRateValuesMessages.deleteReferenceRateValueSuccess),
                description: formatMessage(referenceRateValuesMessages.deleteReferenceRateValueSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...referenceRateValuesMessages.deleteReferenceRateValue },
                subTitle: {
                    ...referenceRateValuesMessages.deleteReferenceRateValueSubtitle,
                    values: { entity: selectedReferenceRateValue.version_number },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...referenceRateValuesMessages.confirmDeleteReferenceRateValue },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: referenceRateValuesMessages.deleteReferenceRateValue,
                callbackFn: () => deleteReferenceRateValue({ referenceRateId, valueId: selectedReferenceRateValue.id }),
            }}
            children={children}
        />
    );
}

export default DeleteReferenceRateValueFormModal;
